import walletApi from 'api/walletApi';
import { useAppSelector, useAppTranslation } from 'app/hooks';
import ErrorIcon from 'assets/icons/ErrorIcon';
import SuccessIcon from 'assets/icons/SuccessIcon';
import { showConfirmModal } from 'components/Modals/CommonModals';
import { CreateVinpayOrderResponseModel } from 'models/wallet';
import React, { useEffect, useRef, useState } from 'react';
import { platformStates } from 'redux/platformSlice';

interface OrderQrTabProps {
  qrCode?: CreateVinpayOrderResponseModel | null;
  visible: boolean;
  closeModal: () => void;
  hiddenQrCode: () => void;
}

const MINUTES = 15;
const SECOND_ON_MINUTE = 60;
const OrderQrTab: React.FunctionComponent<OrderQrTabProps> = (props) => {
  const { qrCode, visible, closeModal, hiddenQrCode } = props;
  const [timeRemaining, setTimeRemaining] = useState(SECOND_ON_MINUTE * MINUTES);
  const { isPhone } = useAppSelector(platformStates);
  const [isCompleteOrder, setIsCompleteOrder] = useState<boolean>(false);
  const backgroundTime = useRef<number | null>();
  const t = useAppTranslation();

  useEffect(() => {
    let timerInterval: NodeJS.Timeout;

    if (visible) {
      timerInterval = setInterval(() => {
        setTimeRemaining((prevTime) => {
          if (prevTime <= 0) {
            clearInterval(timerInterval);
            showCompleteModal('FAILED');
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(timerInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [visible, timeRemaining]);

  useEffect(() => {
    if (visible) {
      setTimeRemaining(SECOND_ON_MINUTE * MINUTES);
    }
  }, [visible]);

  const showCompleteModal = (type: string) => {
    const onOk = () => {
      hiddenQrCode();
    };

    const onCancel = () => {
      closeModal();
    };
    if (!isCompleteOrder) {
      showConfirmModal({
        title: type === 'SUCCESS' ? t('deposit.success.title') : t('deposit.failed.title'),
        content: type === 'SUCCESS' ? t('deposit.success.message') : t('deposit.failed.message'),
        width: 360,
        icon: type === 'SUCCESS' ? <SuccessIcon /> : <ErrorIcon />,
        onOk: onOk,
        okText: t('deposit.fiat.createNew'),
        cancelText: t('sharebet.popup.button-cancel'),
        onCancel,
      });
      setIsCompleteOrder(true);
    }
  };

  const getOrderDetail = async () => {
    if (qrCode) {
      const { ok, body } = await walletApi.getVinpayOrder(qrCode?.orderId);
      if (ok && body) {
        if (body.status !== 'NEW') {
          if (body.status === 'USED') {
            showCompleteModal('SUCCESS');
          } else {
            showCompleteModal('FAILED');
          }
        }
      }
    }
  };

  useEffect(() => {
    let getVinpayInterval: NodeJS.Timeout;
    if (qrCode && document.visibilityState) {
      getVinpayInterval = setInterval(() => {
        if (isCompleteOrder) {
          clearInterval(getVinpayInterval);
        } else {
          getOrderDetail();
        }
      }, 10000);
    }

    return () => {
      clearInterval(getVinpayInterval);
    };
  }, [qrCode, isCompleteOrder]);

  const handleVisibilityChange = () => {
    if (document.visibilityState && backgroundTime.current) {
      const currentTime = Date.now();
      const timeInBackground = Math.floor((currentTime - backgroundTime.current) / 1000);
      setTimeRemaining((prev) => {
        const newTimeRemaining = prev - timeInBackground;
        return newTimeRemaining < 0 ? 0 : newTimeRemaining;
      });
      backgroundTime.current = null;
    } else {
      backgroundTime.current = Date.now();
    }
  };

  const minutes = Math.floor(timeRemaining / 60);
  const seconds = timeRemaining % 60;
  return (
    <div className={`center-content-col gap-3 ${isPhone ? 'px-10' : 'px-50'}`}>
      <img width={250} height={250} src={`data:image/png;base64, ${qrCode?.qrCode}`} />
      <p className="primary-text-color">
        {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
      </p>
    </div>
  );
};

export default OrderQrTab;
